/**
 * Overbooking Service
 *
 * Domain service for overbooking validation.
 * Consolidates overbooking logic from:
 * - AvailabilityConfigurationModal.vue (validateOverbookingFields)
 * - ServiceFormSidebar.vue (validateOverbookingInAvailabilities)
 *
 * This is a pure business logic service - no Vue/UI dependencies.
 */
import { translateApp } from "@/i18n";
// =============================================================================
// OVERBOOKING SERVICE
// =============================================================================
export const overbookingService = {
    /**
     * Check if overbooking is enabled based on field values
     * Overbooking is enabled if both overcapacity AND overflowPrice are set
     */
    isEnabled(config) {
        const hasOvercapacity = config.overcapacity != null && config.overcapacity > 0;
        const hasOverflowPrice = config.overflowPrice != null;
        return hasOvercapacity && hasOverflowPrice;
    },
    /**
     * Check if overbooking can be enabled for this configuration
     * Overbooking is only available for PERSON price type
     */
    canEnable(priceType) {
        return priceType === 'PERSON';
    },
    /**
     * Validate overbooking configuration
     * This consolidates the validation logic from 2 files
     */
    validate(config) {
        const errors = [];
        const hasOvercapacity = config.overcapacity != null;
        const hasOverflowPrice = config.overflowPrice != null;
        // If neither field is set, overbooking is disabled - valid
        if (!hasOvercapacity && !hasOverflowPrice) {
            return { valid: true, errors: [] };
        }
        // Both fields must be set together
        if (hasOvercapacity !== hasOverflowPrice) {
            errors.push(translateApp("calendar.validation.overbooking_pair"));
            return { valid: false, errors };
        }
        // Overbooking only valid for PERSON price type
        if (config.priceType !== 'PERSON') {
            errors.push(translateApp("calendar.validation.overbooking_person_only"));
            return { valid: false, errors };
        }
        // Validate values if both are set
        if (hasOvercapacity && hasOverflowPrice) {
            // Overcapacity (additional spots) must be > 0
            if (config.overcapacity <= 0) {
                errors.push(translateApp("calendar.validation.overbooking_capacity_positive"));
            }
            // Overflow price must be >= 0
            if (config.overflowPrice < 0) {
                errors.push(translateApp("calendar.validation.overflow_price_non_negative"));
            }
        }
        return {
            valid: errors.length === 0,
            errors,
        };
    },
    /**
     * Validate multiple availability items for overbooking
     * Used when saving a service with multiple availability configurations
     */
    validateAll(items) {
        const allErrors = [];
        for (let i = 0; i < items.length; i++) {
            const result = this.validate(items[i]);
            if (!result.valid) {
                const prefix = items.length > 1 ? `Horario ${i + 1}: ` : '';
                allErrors.push(...result.errors.map((e) => prefix + e));
            }
        }
        return {
            valid: allErrors.length === 0,
            errors: allErrors,
        };
    },
    /**
     * Get default overbooking values when enabling
     * Based on logic from AvailabilityConfigurationModal watch handler
     * Note: overcapacity represents ADDITIONAL spots, not total capacity
     */
    getDefaultValues(standardCapacity, netPrice) {
        // Default overcapacity: 50% of standard (min 1, max 10 additional spots)
        const overcapacity = Math.max(1, Math.min(Math.ceil(standardCapacity * 0.5), 10));
        // Default overflow price: net price * 1.5 (50% premium)
        const overflowPrice = Math.ceil(netPrice * 1.5);
        return { overcapacity, overflowPrice };
    },
    /**
     * Calculate extra spots available through overbooking
     * Note: overcapacity IS the extra spots (additional capacity)
     */
    getExtraSpots(config) {
        if (!this.isEnabled(config))
            return 0;
        return config.overcapacity || 0;
    },
    /**
     * Get overbooking status summary
     */
    getStatus(config) {
        return {
            isEnabled: this.isEnabled(config),
            canEnable: this.canEnable(config.priceType),
            standardCapacity: config.standardCapacity,
            overcapacity: config.overcapacity,
            overflowPrice: config.overflowPrice,
            extraSpots: this.getExtraSpots(config),
        };
    },
    /**
     * Check if a booking count would fall into overbooking range
     * Note: overcapacity represents ADDITIONAL spots, so total = standard + overcapacity
     */
    isOverbooking(bookedCount, standardCapacity, overcapacity) {
        if (overcapacity == null)
            return false;
        const totalCapacity = standardCapacity + overcapacity;
        return bookedCount > standardCapacity && bookedCount <= totalCapacity;
    },
    /**
     * Get how many spots are left (considering overbooking)
     * Note: overcapacity represents ADDITIONAL spots, so total = standard + overcapacity
     */
    getSpotsRemaining(bookedCount, standardCapacity, overcapacity) {
        const totalCapacity = standardCapacity + (overcapacity || 0);
        const standardRemaining = Math.max(0, standardCapacity - bookedCount);
        const overflowRemaining = overcapacity
            ? Math.max(0, totalCapacity - Math.max(bookedCount, standardCapacity))
            : 0;
        return {
            standard: standardRemaining,
            overflow: overflowRemaining,
            total: Math.max(0, totalCapacity - bookedCount),
        };
    },
};
// =============================================================================
// DEFAULT EXPORT
// =============================================================================
export default overbookingService;
